/**
 * @fileoverview Message renderer component that parses and renders inline interactive components.
 * Supports buttons, links, badges, code snippets, and other interactive elements within chat messages.
 *
 * @module components/chat/InlineMessageRenderer
 */

import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { parseInlineComponents } from '../../utils/chat/inlineComponentParser'
import { getInlineComponent } from './inline'

/**
 * InlineMessageRenderer component - Renders chat messages with embedded interactive components.
 * Parses message content for [[componentId]] markers and replaces them with actual React components.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.message - Message object containing content and component definitions
 * @param {string} [props.message.content] - Message text content
 * @param {string} [props.message.type] - Message type (e.g., 'clickableresponse')
 * @param {Object} [props.message.components] - Map of component IDs to component definitions
 * @param {Function} [props.onComponentAction] - Callback for component interactions
 *
 * @returns {JSX.Element} Rendered message with inline components
 *
 * @description
 * Supports two message formats:
 *
 * 1. Component markers format:
 *    - Text contains [[componentId]] markers
 *    - Components defined in message.components object
 *    - Example: "Click [[button1]] to continue"
 *
 * 2. Clickable response format:
 *    - message.type === 'clickableresponse'
 *    - Used for reference citations and interactive elements
 *    - Currently renders as plain text (references feature pending)
 *
 * Component types supported:
 * - button: Interactive buttons with actions
 * - link: External/internal links
 * - badge: Status/info badges
 * - code: Inline code snippets
 * - codeblock: Multi-line code blocks
 *
 * @example
 * <InlineMessageRenderer
 *   message={{
 *     content: "Click [[btn1]] to learn more",
 *     components: {
 *       btn1: { type: 'button', label: 'Learn More', action: 'learn' }
 *     }
 *   }}
 *   onComponentAction={(action, data) => handleAction(action, data)}
 * />
 */
export const InlineMessageRenderer = ({ message, onComponentAction, onSentenceClick }) => {
  const parts = useMemo(() => {
    // Handle new sentence-based structure (should be handled by ChatMessage directly, but keep for safety)
    if (message.hasClickableSentences && message.sentences) {
      // This structure is now handled directly by ChatMessage, but we can provide a fallback
      return message.sentences.map((sentence, index) => ({
        type: 'clickable-text',
        content: sentence.text,
        sentenceId: sentence.sentenceUUID,
        messageId: message.messageId,
        conversationId: message.conversationId,
        key: `sentence-${index}`,
      }))
    }

    // Handle clickable response type or messages with sentence_id
    if (message.type === 'clickableresponse' || message.sentence_id) {
      const text = message.content || message.message || ''
      const components = message.components || {}

      // Parse for inline components first
      const parsedParts = parseInlineComponents(text, components)

      // If we have a sentence_id, wrap text parts (not components) in clickable spans
      if (message.sentence_id) {
        return parsedParts.map((part) => {
          if (part.type === 'text') {
            // Make text segments clickable for references
            return {
              ...part,
              type: 'clickable-text',
              sentenceId: message.sentence_id,
              messageId: message.message_id,
              conversationId: message.conversation_id,
            }
          }
          // Leave components as-is
          return part
        })
      }

      return parsedParts
    }

    // Parse regular messages with component markers (Option 1)
    const text = message.content || ''
    const components = message.components || {}
    return parseInlineComponents(text, components)
  }, [message])

  return (
    <div className="inline-message-content">
      {parts.map((part, index) => {
        if (part.type === 'clickable-text') {
          return (
            <span
              key={index}
              className="cursor-pointer bg-gray-700/20 px-1 py-0.5 rounded hover:bg-gray-600/30 hover:text-gray-200 transition-all duration-200"
              onClick={() =>
                onSentenceClick?.(part.sentenceId, part.messageId, part.conversationId)
              }
              title="Click to view references"
            >
              {part.content}
            </span>
          )
        }

        if (part.type === 'text') {
          return <span key={index}>{part.content}</span>
        }

        if (part.type === 'component') {
          const Component = getInlineComponent(part.componentType)
          if (!Component) {
            // Fallback to text if component not found
            return <span key={index}>{part.props.text || '[Component]'}</span>
          }

          // Apply different styling based on component type
          const componentClassName =
            part.componentType === 'heading' || part.componentType === 'image'
              ? undefined
              : 'inline-flex align-middle mx-1'

          return (
            <Component
              key={index}
              {...part.props}
              onAction={onComponentAction}
              className={componentClassName}
            />
          )
        }

        return null
      })}
    </div>
  )
}

InlineMessageRenderer.propTypes = {
  message: PropTypes.shape({
    type: PropTypes.string,
    content: PropTypes.string,
    message: PropTypes.string,
    components: PropTypes.object,
    sentence_id: PropTypes.string,
    message_id: PropTypes.string,
    conversation_id: PropTypes.string,
    // New sentence-based structure
    hasClickableSentences: PropTypes.bool,
    sentences: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        sentenceUUID: PropTypes.string.isRequired,
      })
    ),
    messageId: PropTypes.string,
    conversationId: PropTypes.string,
  }).isRequired,
  onComponentAction: PropTypes.func,
  onSentenceClick: PropTypes.func,
}

InlineMessageRenderer.defaultProps = {
  onComponentAction: () => {},
  onSentenceClick: undefined,
}
